import { animate, style, transition, trigger } from "@angular/animations";
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, Pipe, PipeTransform, SimpleChanges, ViewChild } from "@angular/core";
import { VAutocompleteComponent } from "@directives/_forms/v-autocomplete/v-autocomplete.component";
import { AccordianEntryComponent } from "@directives/_layout/accordian/accordian-entry/accordian-entry.component";
import { DynamicReportService } from "@reports/_services/dynamicReports.service";
import { IColumnHeader } from "@reports/models/IColumnHeader";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { copy } from "angular";
import { ReportTableRowDataSource } from "../../enums/ReportTableRowDataSource";
import "./report-column-headers-selector.component.scss";
import { TriggerValueOperator } from "@app/main/modules/alerts/_enums/alerts.enums";

@Pipe({
    name: "filterTitleRowColumnHeader"
})
export class FilterTitleRowColumnHeaderPipe implements PipeTransform {
    transform(columnHeaders: IColumnHeader[]): IColumnHeader[] {
        if (!columnHeaders?.length) {
            return [];
        }

        return columnHeaders.filter(columnHeader => !columnHeader.IsRowTitle);
    }
}

@Component({
    selector: "report-column-headers-selector",
    template: require("./report-column-headers-selector.component.html"),
    animations: [
        trigger("columnListAnimation", [
            transition(":enter", [
                style({ opacity: 0, transform: "translateY(-20px)" }),
                animate("300ms ease-out", style({ opacity: 1, transform: "translateY(0px)" }))
            ]),
            transition(":leave", [
                animate("300ms ease-out", style({ opacity: 0, height: 0 }))
            ]),
            transition("* <=> *", [
                animate("300ms ease-out")
            ])
        ])
    ]
})
export class ReportColumnHeadersSelectorComponent implements OnChanges {

    @Input() primaryDataSource: ReportTableRowDataSource;
    @Input() allowedColumnHeaders: IColumnHeader[];
    @Input() columnHeaders: IColumnHeader[];
    @Output() columnHeadersChange = new EventEmitter<IColumnHeader[]>();

    remainingAllowedColumns: IColumnHeader[];

    @ViewChild("columnHeaderAutocomplete", { static: false }) columnHeaderAutocomplete: VAutocompleteComponent<IColumnHeader>;

    trackColumnHeaders(index: number, columnHeader: IColumnHeader) {
        return columnHeader.Id;
    }

    constructor(
        private readonly dynamicReportService: DynamicReportService,
        private readonly translationService: TranslationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly veriDate: VeriDate
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes["columnHeaders"]) {

            this.dynamicReportService.updateFilterOptionsForColumnHeaders(this.columnHeaders);
            this.moveTitleRowToTop(this.columnHeaders);

        }

        if (changes["allowedColumnHeaders"]) {
            this.dynamicReportService.updateFilterOptionsForColumnHeaders(this.allowedColumnHeaders);
        }

        this.updateRemainingAllowedColumnHeaders();
    }

    onMovedColumn(columnHeader: IColumnHeader, direction: "up" | "down" = "down"): void {

        const indexOfColumnHeader = this.columnHeaders.indexOf(columnHeader);

        if (indexOfColumnHeader === -1) {
            console.warn("Couldn't find column header when trying to move/re-order");
            return;
        }

        const newIndex = (direction === "up") ? indexOfColumnHeader - 1 : indexOfColumnHeader + 1;

        this.moveColumnHeader(indexOfColumnHeader, newIndex);
    }

    onColumnHeaderSelected(columnHeaderSelected: IColumnHeader): void {
        if (!columnHeaderSelected) {
            return;
        }

        const columnHeaderCopy: IColumnHeader = copy(columnHeaderSelected);

        this.columnHeaderAutocomplete.clearInput();

        const indexOfExisting = this.columnHeaders.findIndex(columnHeader => columnHeader.uniqueId === columnHeaderCopy.uniqueId);

        if (indexOfExisting > -1) {
            console.warn("Column header already exists");
            return;
        }

        this.columnHeaders.push(columnHeaderCopy);

        this.updateRemainingAllowedColumnHeaders();
        this.forceArrayChanges();
    }

    onStartedDragColumnHeader(event: MouseEvent | TouchEvent, columnHeader: IColumnHeader, accordianEntry: AccordianEntryComponent): void {
        accordianEntry.close();
    }

    onClickEditColumnHeader(columnHeader: IColumnHeader): void {
        columnHeader.showEdit = !columnHeader.showEdit;
    }

    onFilterTimeChanged(columnHeader: IColumnHeader, value: number, type: "hour" | "minute"): void {
        if (!columnHeader?.filterOptions) {
            console.warn("No filter options found on column header");
            return;
        }

        const filterOptionsProp = (type === "hour") ? "filterTimeHour" : "filterTimeMinute";

        columnHeader.filterOptions[filterOptionsProp] = value;

        const hour: number = (isNaN(columnHeader.filterOptions.filterTimeHour)) ? 0 : columnHeader.filterOptions.filterTimeHour;
        const minute: number = (isNaN(columnHeader.filterOptions.filterTimeMinute)) ? 0 : columnHeader.filterOptions.filterTimeMinute;
        if (hour == 0 && minute == 0 && columnHeader.FilterValueOperator === TriggerValueOperator.Equals) {
            columnHeader.FilterValue = null;
        }
        else {
            columnHeader.FilterValue = this.veriDate.createMoment(new Date()).hour(hour).minute(minute).second(0).format("HH:mm:ss");
        }

        this.onFormValueChanged();
    }

    onBlurTitle(columnHeader: IColumnHeader): void {
        if (!columnHeader.Title) {
            this.revertTitle(columnHeader);
        }
    }

    revertTitle(columnHeader: IColumnHeader): void {
        columnHeader.Title = columnHeader.DefaultTitle;
    }

    columnHeaderFilter = (columnHeader: IColumnHeader, text: string): boolean => {
        return this.columnHeaderSearchPredicate(columnHeader, text);
    }

    columnHeaderSearchPredicate(columnHeader: IColumnHeader, text: string): boolean {
        const category = columnHeader.dataSourceDefinition.TranslatedDisplayName ? columnHeader.dataSourceDefinition.TranslatedDisplayName : "Unknown";
        const alreadyAdded: boolean = !!this.columnHeaders.find(header => header.Id === columnHeader.Id);

        const matchesTitle: boolean = columnHeader.Title.toLowerCase().includes(text.toLowerCase());
        const matchesDefaultTitle: boolean = columnHeader.DefaultTitle.toLowerCase().includes(text.toLowerCase());
        const matchesCategory: boolean = category.toLowerCase().includes(text.toLowerCase());

        const foundMatch = matchesTitle || matchesDefaultTitle || matchesCategory;

        return !alreadyAdded && foundMatch;
    }

    deleteColumn(columnHeader: IColumnHeader): void {
        const indexOfColumn = this.columnHeaders.indexOf(columnHeader);

        if (indexOfColumn === -1) {
            return;
        }

        this.columnHeaders.splice(indexOfColumn, 1);

        this.updateRemainingAllowedColumnHeaders();
        this.forceArrayChanges();
    }

    headerFilter(header: IColumnHeader, text: string): boolean {
        return header.ColumnDefinition.TranslatedTitle.toLowerCase().indexOf(text) >= 0;
    }

    onFormValueChanged(): void {
        this.forceArrayChanges();
    }

    // ---

    private moveColumnHeader(indexOfHeader: number, newIndex: number): void {
        const removedColumn = this.columnHeaders.splice(indexOfHeader, 1)[0];

        this.columnHeaders.splice(newIndex, 0, removedColumn);

        this.forceArrayChanges();
    }

    private forceArrayChanges(): void {
        this.moveTitleRowToTop(this.columnHeaders);

        this.columnHeadersChange.next([].concat(this.columnHeaders));
    }

    private updateRemainingAllowedColumnHeaders(): void {

        const filteredColumnHeaders = this.allowedColumnHeaders.filter(allowedColumnHeader => {
            return !this.columnHeaders.find(addedColumnHeader => addedColumnHeader.Id === allowedColumnHeader.Id);
        });

        this.remainingAllowedColumns = [].concat(filteredColumnHeaders);
    }

    private moveTitleRowToTop(columnHeaders: IColumnHeader[]): void {
        if (!columnHeaders?.length) {
            return;
        }

        const indexOfTitleRow = this.columnHeaders.findIndex(columnHeader => columnHeader.IsRowTitle);

        if (indexOfTitleRow < 1) {
            return;
        }

        this.columnHeaders.splice(0, 0, this.columnHeaders.splice(indexOfTitleRow, 1)[0]);
    }
}
